import React, { useContext, useMemo, useState } from 'react';
import type {
  CurrentCustomerType,
  DataCurrentCustomerType,
} from 'context/global-customer-creation/types';
import CurrentCustomer from 'context/global-customer-creation/context';

import { getClientDetail } from 'api/client';
import type { ClientDetailData } from 'api/client/models';

import { SCREENS_KEYS, ADMIN_SOURCE_TYPES } from 'constants/constants';

import Customer, { ValueCustumer } from './context';

type Props = {
  children: React.ReactNode;
  sourceType: 'clientes' | 'fondos' | 'dashboard';
};

const CustomerProvider = ({ children, sourceType }: Props) => {
  const [customer, setCustomer] = useState<
    ClientDetailData | null | undefined
  >();

  const isDashboard = sourceType === ADMIN_SOURCE_TYPES.DASHBOARD;
  const effectiveSourceType = isDashboard
    ? ADMIN_SOURCE_TYPES.CLIENTES
    : sourceType;

  const { updateCurrentCustomer, resetCurrentCustomer } = useContext(
    CurrentCustomer,
  ) as CurrentCustomerType;

  const resetCustomer = () => {
    setCustomer(undefined);
    resetCurrentCustomer();
  };

  const {
    screenKeyForPositionsPortafolio,
    screenKeyForPositionsMovements,
    screenKeyForPositionsYields,
  } = useMemo(() => {
    if (isDashboard) {
      return {
        screenKeyForPositionsPortafolio: SCREENS_KEYS.dashboard,
        screenKeyForPositionsMovements: SCREENS_KEYS.dashboard,
        screenKeyForPositionsYields: SCREENS_KEYS.dashboard,
      };
    }

    return {
      screenKeyForPositionsPortafolio:
        effectiveSourceType === ADMIN_SOURCE_TYPES.CLIENTES
          ? SCREENS_KEYS.positionsPortafolio
          : SCREENS_KEYS.positionsFundsPortfolio,
      screenKeyForPositionsMovements:
        effectiveSourceType === ADMIN_SOURCE_TYPES.CLIENTES
          ? SCREENS_KEYS.positionsMovements
          : SCREENS_KEYS.positionsFundsMovements,
      screenKeyForPositionsYields:
        effectiveSourceType === ADMIN_SOURCE_TYPES.CLIENTES
          ? SCREENS_KEYS.positionsYields
          : SCREENS_KEYS.positionsFundsYields,
    };
  }, [effectiveSourceType, isDashboard]);

  const updateCustomer = async ({
    profile,
    clientId,
    contractId,
    fullName,
    clientRole,
    onboardingStep,
    profileDescription,
    taxPersonType,
  }: ValueCustumer) => {
    const { success, data, errorMessage } = await getClientDetail({
      clientId,
      taxPersonType,
    });

    if (success) {
      setCustomer(data);
      updateCurrentCustomer({
        profile,
        clientId,
        contractId,
        fullName,
        onboardingComplete: true,
        clientRole,
        onboardingStep,
        profileDescription,
        taxPersonType,
      } as DataCurrentCustomerType);
      return { success, data };
    }
    return { success, errorMessage };
  };

  const getClientDetailData = async ({
    clientId,
    taxPersonType,
  }: {
    clientId: number;
    taxPersonType: string;
  }) => {
    const respClientDetail = await getClientDetail({
      clientId,
      taxPersonType,
    });

    return respClientDetail;
  };

  return (
    <Customer.Provider
      value={{
        customer,
        setCustomer,
        updateCustomer,
        updateCurrentCustomer,
        getClientDetailData,
        resetCustomer,
        sourceType: effectiveSourceType,
        screenKeyForPositionsPortafolio,
        screenKeyForPositionsMovements,
        screenKeyForPositionsYields,
      }}
    >
      {children}
    </Customer.Provider>
  );
};

export default CustomerProvider;
